import { useState } from 'react'
import { type CoachingPlan } from '../../../../../models/CoachingPlan'
import { isValidPositiveNumber } from '../../../../../utils/isValidNumber'
import { toast } from 'react-toastify'
import { CoachingPlanProgress } from '../../../../../models/CoachingPlanProgress'
import { UpdateCoachingPlan } from '../../../../../services/AnnualPlanService'
import { Box, FormControl, FormControlLabel, FormLabel, InputAdornment, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material'
import { Modal } from '../../../../customComponents/Modal'

interface CoachingPlanTrackingFormValues {
  progress: string
  progressNotes: string
  implementationNotes: string
  budgetSpent: string | number | null
}

interface Props {
  open: boolean
  onClose: () => void
  onSaved: () => void
  coachingPlan: CoachingPlan
  implementationPhase: boolean
}
export const CoachingPlanTrackingModal = (props: Props): JSX.Element => {
  const [formValues, setFormValues] = useState<CoachingPlanTrackingFormValues>({
    progress: props.coachingPlan.progress ?? '',
    progressNotes: props.coachingPlan.progressNotes ?? '',
    implementationNotes: props.coachingPlan.implementationNotes ?? '',
    budgetSpent: props.coachingPlan.budgetSpent ?? null
  })
  const [submitted, setSubmitted] = useState<boolean>(false)

  const formValidators: Record<keyof CoachingPlanTrackingFormValues, { required: boolean, isValid: boolean }> = {
    progress: { required: true, isValid: formValues.progress.trim() !== '' && Object.values(CoachingPlanProgress).includes(formValues.progress as CoachingPlanProgress) },
    progressNotes: { required: true, isValid: formValues.progressNotes.trim() !== '' },
    implementationNotes: { required: true, isValid: formValues.implementationNotes.trim() !== '' },
    budgetSpent: { required: false, isValid: formValues.budgetSpent == null || formValues.budgetSpent === '' || isValidPositiveNumber(formValues.budgetSpent) }
  }
  const hasInvalidFormValues = Object.values(formValidators).some(v => !v.isValid)

  const handleFieldUpdate = (e: { target: { name: string, value: string } }): void => {
    const { name, value } = e.target
    setFormValues({ ...formValues, [name]: value })
  }

  const handleSubmit = async (): Promise<void> => {
    setSubmitted(true)

    if (hasInvalidFormValues) {
      toast.error('Please fill out required fields')
      return
    }

    const updateCoachingPlan: CoachingPlan = {
      ...props.coachingPlan,
      progress: formValues.progress as CoachingPlanProgress,
      progressNotes: formValues.progressNotes,
      implementationNotes: props.implementationPhase ? formValues.implementationNotes : undefined,
      budgetSpent: isValidPositiveNumber(formValues.budgetSpent) ? Number(formValues.budgetSpent) : null,
      isImplementationPhase: props.implementationPhase
    }

    const { success, errors } = await UpdateCoachingPlan(updateCoachingPlan)

    if (!success) {
      toast.error(`Failed to update coaching plan: ${errors.join(', ')}`)
      return
    }

    toast.success('Coaching Plan Tracked')
    props.onSaved()
    props.onClose()
  }

  const renderTrackingFields = (): JSX.Element => {
    return (
      <Stack gap='1rem'>
        <Typography variant='h5'>Progress</Typography>

        <FormControl
          variant='filled'
          required={formValidators.progress.required}
          error={submitted && !formValidators.progress.isValid}
        >
          <FormLabel id="progressLabel">Please select your current progress:</FormLabel>
          <RadioGroup
            aria-labelledby="progressLabel"
            name="progress"
            data-testid='progress'
            value={formValues.progress}
            onChange={handleFieldUpdate}
          >
            {Object.entries(CoachingPlanProgress).map(([key, progress]) => (
              <FormControlLabel
                key={progress}
                value={progress}
                control={<Radio />}
                label={progress}
                data-testid={`progress-${key}`}
              />
            ))}
          </RadioGroup>
        </FormControl>

        <TextField
          fullWidth
          multiline
          hiddenLabel
          rows={3}
          variant='filled'
          id='progressNotes'
          name='progressNotes'
          data-testid='progressNotes'
          value={formValues.progressNotes}
          onChange={handleFieldUpdate}
          placeholder='Provide progress notes here.'
          required={formValidators.progressNotes.required}
          error={submitted && !formValidators.progressNotes.isValid}
          // manual styling to show error state with hidden label
          sx={{
            '& .MuiInputBase-input': {
              color: theme => submitted && !formValidators.progressNotes.isValid ? theme.palette.error.main : undefined,
              '&::placeholder': {
                opacity: submitted && !formValidators.progressNotes.isValid ? 1 : undefined
              }
            }
          }}
        />

        <Typography variant='h5'>Notes</Typography>
        <TextField
          fullWidth
          multiline
          hiddenLabel
          rows={3}
          variant='filled'
          id='implementationNotes'
          name='implementationNotes'
          data-testid='implementationNotes'
          value={formValues.implementationNotes}
          onChange={handleFieldUpdate}
          placeholder='Provide any further information related to the implementation of your Coaching Plan.'
          required={formValidators.implementationNotes.required}
          error={submitted && !formValidators.implementationNotes.isValid}
          // manual styling to show error state with hidden label
          sx={{
            '& .MuiInputBase-input': {
              color: theme => submitted && !formValidators.implementationNotes.isValid ? theme.palette.error.main : undefined,
              '&::placeholder': {
                opacity: submitted && !formValidators.implementationNotes.isValid ? 1 : undefined
              }
            }
          }}
        />

        <Typography variant='h5'>Budget</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <Typography variant='body1'>
            How much funding was actually used?
            <Typography component='span' variant='caption' fontStyle='italic' color='var(--grey-500)'> Optional</Typography>
          </Typography>
          <TextField
            id='budgetSpent'
            sx={{ mt: '.75em', width: '250px' }}
            variant='filled'
            onChange={handleFieldUpdate}
            label={'Enter Amount'}
            data-testid='budgetSpent'
            InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
            name='budgetSpent'
            required={formValidators.budgetSpent.required}
            error={submitted && !formValidators.budgetSpent.isValid}
            value={formValues.budgetSpent ?? ''}
          />
        </Box>
      </Stack>
    )
  }

  return (
    <Modal
      title='Coaching Plan Tracking'
      open={props.open}
      data-testid='coachingPlanTrackingModal'
      width='480px'
      confirmButtonText='Save'
      cancelButtonText='Cancel'
      onClose={props.onClose}
      onConfirm={handleSubmit}
      bodyContent={<Stack gap='1rem' m='24px 0'>{renderTrackingFields()}</Stack>}
    />
  )
}
